/**
 * TradeDetailViewModal (KAN-169)
 *
 * Read-only modal that displays trade data in a layout that replicates
 * the Trade Booking form's visual style (dark cards, form-input styling,
 * toggle buttons). Used from QuoteApprovalDetailPanel.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { quoteApprovalService } from '../../services/api/quoteApprovalService';
import './TradeDetailViewModal.css';

interface Props {
  trade: Record<string, any>;
  entityId: string;
  onApproveSuccess: () => void;
  onRejectSuccess: () => void;
  onError: (message: string) => void;
  onClose: () => void;
}

const TradeDetailViewModal: React.FC<Props> = ({
  trade,
  entityId,
  onApproveSuccess,
  onRejectSuccess,
  onError,
  onClose,
}) => {
  const { t } = useTranslation();
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      const response = await quoteApprovalService.approveQuote(entityId, trade.id);
      if (response.success) {
        onApproveSuccess();
      } else {
        onError(response.message || 'Failed to approve');
      }
    } catch (err: any) {
      onError(err.message || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      onError(t('modifications.detail.rejectReasonRequired', 'Rejection reason is required'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await quoteApprovalService.rejectQuote(entityId, trade.id, rejectReason.trim());
      if (response.success) {
        onRejectSuccess();
      } else {
        onError(response.message || 'Failed to reject');
      }
    } catch (err: any) {
      onError(err.message || 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  const formatNumber = (value: any): string => {
    if (value == null || value === '') return '-';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (value: any): string => {
    if (!value) return '-';
    const s = String(value).substring(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : s;
  };

  const isForward = trade.ProductType === 'Forward';
  const isAsiatico = !!trade.IsAverageRateForward;
  const isForwardStarting = !!trade.IsForwardStarting;
  const isEntregaFisica = trade.SettlementType === 'Entrega Física';
  const isCompensacion = trade.SettlementType === 'Compensación';

  const directionRaw = (trade.Direction || '').toLowerCase();
  const isBuy = directionRaw === 'buy' || directionRaw === 'compra';
  const isSell = directionRaw === 'sell' || directionRaw === 'venta';

  // Fixing schedule dates (Asiático)
  const fixingDates: string[] = (() => {
    const schedule = trade.AverageRateFixingSchedule || trade.averageRateFixingSchedule;
    if (!Array.isArray(schedule)) return [];
    return schedule.map((entry: any) => {
      const d = typeof entry === 'string' ? entry : entry?.date || entry?.Date;
      return formatDate(d);
    }).filter((d: string) => d !== '-');
  })();

  /** Reusable read-only field: label + styled value box */
  const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div className="trade-detail-readonly-value">{value}</div>
    </div>
  );

  return (
    <div className="modifications-modal-overlay" onClick={onClose}>
      <div
        className="modifications-modal trade-detail-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modifications-modal-header">
          <span className="modifications-modal-title">
            {t('modifications.quoteApproval.tradeDetailTitle', 'Trade Detail')}
            {trade.TradeNumber ? ` — #${trade.TradeNumber}` : ''}
          </span>
          <button className="modifications-modal-close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Body — 2-column grid of section cards */}
        <div className="modifications-modal-body">
          <div className="trade-detail-form">

            {/* ── Section 1: Trade Details ── */}
            <div className="form-section">
              <div className="section-title">
                {t('tradeBooking.tradeDetails.title', 'Trade Details')}
              </div>

              {/* Trade Type toggle */}
              <div className="form-group">
                <label className="form-label">{t('tradeBooking.tradeType.label', 'Trade Type')}</label>
                <div className="trade-detail-toggle-group">
                  <div className={`trade-detail-toggle-btn${!isForward ? ' active' : ''}`}>
                    {t('tradeBooking.tradeType.spot', 'Spot')}
                  </div>
                  <div className={`trade-detail-toggle-btn${isForward ? ' active' : ''}`}>
                    {t('tradeBooking.tradeType.forward', 'Forward')}
                  </div>
                </div>
                {/* Forward sub-type badges */}
                {isForwardStarting && (
                  <span className="trade-detail-badge starting">{t('tradeBooking.forwardStarting.badge', 'Starting')}</span>
                )}
                {isAsiatico && (
                  <span className="trade-detail-badge asiatico">{t('tradeBooking.forwardAsiatico.badge', 'Asiático')}</span>
                )}
              </div>

              {/* Direction toggle */}
              <div className="form-group">
                <label className="form-label">{t('tradeBooking.tradeDetails.direction', 'Direction')}</label>
                <div className="trade-detail-toggle-group">
                  <div className={`trade-detail-toggle-btn buy${isBuy ? ' active' : ''}`}>
                    {t('tradeBooking.tradeDetails.buy', 'Buy')}
                  </div>
                  <div className={`trade-detail-toggle-btn sell${isSell ? ' active' : ''}`}>
                    {t('tradeBooking.tradeDetails.sell', 'Sell')}
                  </div>
                </div>
              </div>

              {/* Currency pair row */}
              <div className="trade-detail-row">
                <ReadOnlyField
                  label={t('tradeBooking.tradeDetails.currency1', 'Currency 1')}
                  value={trade.Currency1 || '-'}
                />
                <ReadOnlyField
                  label={t('tradeBooking.tradeDetails.currency2', 'Currency 2')}
                  value={trade.Currency2 || '-'}
                />
              </div>

              {/* Amount */}
              <ReadOnlyField
                label={`${t('tradeBooking.tradeDetails.amount', 'Amount')} ${trade.Currency1 || ''}`}
                value={formatNumber(trade.QuantityCurrency1)}
              />

              {/* Rate breakdown for Forward */}
              {isForward && (
                <div className="trade-detail-rate-breakdown">
                  <div className="form-group">
                    <span className="rate-breakdown-label">
                      {t('tradeBooking.tradeDetails.spotPrice', 'Spot Price')}
                    </span>
                    <div className="trade-detail-readonly-value">
                      {isForwardStarting
                        ? (trade.SpotPrice != null ? formatNumber(trade.SpotPrice) : t('tradeBooking.forwardStarting.toBeFixed', 'To Be Fixed'))
                        : formatNumber(trade.SpotPrice)}
                    </div>
                  </div>
                  <div className="form-group">
                    <span className="rate-breakdown-label">
                      {t('tradeBooking.tradeDetails.forwardPoints', 'Forward Points')}
                    </span>
                    <div className="trade-detail-readonly-value">
                      {trade.ForwardPoints != null ? formatNumber(trade.ForwardPoints) : '-'}
                    </div>
                  </div>
                </div>
              )}

              {/* Price */}
              <ReadOnlyField
                label={t('tradeBooking.tradeDetails.price', 'Price')}
                value={isForwardStarting ? 'TBD' : formatNumber(trade.Price)}
              />

              {/* Contra Amount */}
              <ReadOnlyField
                label={`${t('tradeBooking.fields.contraAmount', 'Contra Amount')} ${trade.Currency2 || ''}`}
                value={isForwardStarting ? 'TBD' : formatNumber(trade.QuantityCurrency2)}
              />

              {/* Parties */}
              <div className="trade-detail-row">
                <ReadOnlyField
                  label={t('tradeBooking.tradeDetails.ourParty', 'Our Party')}
                  value={trade.OurParty || '-'}
                />
                <ReadOnlyField
                  label={t('tradeBooking.tradeDetails.counterparty', 'Counterparty')}
                  value={trade.CounterpartyName || '-'}
                />
              </div>
            </div>

            {/* ── Section 2: Dates ── */}
            <div className="form-section">
              <div className="section-title">
                {t('tradeBooking.sections.dates', 'Dates')}
              </div>

              <ReadOnlyField
                label={t('tradeBooking.administrative.tradeDate', 'Trade Date')}
                value={formatDate(trade.TradeDate)}
              />

              <ReadOnlyField
                label={t('tradeBooking.forwardFields.valueDate', 'Value Date')}
                value={formatDate(trade.ValueDate)}
              />

              {isForward && (
                <ReadOnlyField
                  label={t('tradeBooking.forwardFields.maturity', 'Maturity')}
                  value={trade.Maturity || '-'}
                />
              )}

              <ReadOnlyField
                label={t('tradeBooking.forwardFields.expiryDate', 'Expiry Date')}
                value={formatDate(trade.ExpiryDate || trade.MaturityDate)}
              />

              {/* Payment date — Compensación */}
              {!isEntregaFisica && (
                <ReadOnlyField
                  label={t('tradeBooking.fields.paymentDate', 'Payment Date')}
                  value={formatDate(trade.PaymentDate)}
                />
              )}

              {/* Valuta dates — Entrega Física */}
              {isEntregaFisica && (
                <>
                  <ReadOnlyField
                    label={t('tradeBooking.dates.currency1PaymentDate', 'Currency 1 Payment Date (Valuta)')}
                    value={formatDate(trade.Currency1PaymentDate)}
                  />
                  <ReadOnlyField
                    label={t('tradeBooking.dates.currency2PaymentDate', 'Currency 2 Payment Date (Valuta)')}
                    value={formatDate(trade.Currency2PaymentDate)}
                  />
                </>
              )}

              {/* Bank Trade Number — extra info that fits here */}
              {(trade.bankTradeNumber || trade.BankTradeNumber) && (
                <ReadOnlyField
                  label={t('modifications.columns.bankTradeNumber', 'Bank Trade #')}
                  value={trade.bankTradeNumber || trade.BankTradeNumber}
                />
              )}
            </div>

            {/* ── Section 3: Fixing & Settlement (Forward only) ── */}
            {isForward && (
              <div className="form-section">
                <div className="section-title">
                  {t('tradeBooking.sections.fixing', 'Fixing & Expiry')}
                </div>

                <ReadOnlyField
                  label={t('tradeBooking.forwardFields.settlementType', 'Settlement Type')}
                  value={trade.SettlementType || '-'}
                />

                {isCompensacion && (
                  <ReadOnlyField
                    label={t('tradeBooking.forwardFields.settlementCurrency', 'Settlement Currency')}
                    value={trade.SettlementCurrency || '-'}
                  />
                )}

                {!isEntregaFisica && (
                  <>
                    <ReadOnlyField
                      label={t('tradeBooking.forwardFields.fixingReference', 'Fixing Reference')}
                      value={trade.FixingReference || '-'}
                    />
                    <ReadOnlyField
                      label={t('tradeBooking.forwardFields.fixingOffset', 'Fixing Offset')}
                      value={trade.FixingOffset != null ? String(trade.FixingOffset) : '-'}
                    />
                  </>
                )}

                {/* Asiático fixing schedule */}
                {isAsiatico && fixingDates.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">
                      {t('tradeBooking.forwardAsiatico.fixingSchedule', 'Fixing Schedule')}
                    </label>
                    <div className="trade-detail-fixing-dates">
                      {fixingDates.map((d, i) => (
                        <span key={i} className="trade-detail-fixing-date">{d}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Section 4: Classification & Notes ── */}
            <div className="form-section">
              <div className="section-title">
                {t('tradeBooking.sections.classificationAndNotes', 'Classification & Notes')}
              </div>

              <div className="trade-detail-row">
                <ReadOnlyField
                  label={t('tradeBooking.classification.grouping1', 'Grouping 1')}
                  value={trade.Grouping1 || '-'}
                />
                <ReadOnlyField
                  label={t('tradeBooking.classification.grouping2', 'Grouping 2')}
                  value={trade.Grouping2 || '-'}
                />
              </div>

              <ReadOnlyField
                label={t('tradeBooking.classification.grouping3', 'Grouping 3')}
                value={trade.Grouping3 || '-'}
              />

              <ReadOnlyField
                label={t('tradeBooking.administrative.hedgeObject', 'Hedge Object')}
                value={trade.HedgeObject || '-'}
              />

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">
                  {t('tradeBooking.administrative.notes', 'Notes / Comments')}
                </label>
                <div className="trade-detail-notes-value">
                  {trade.Notes || trade.notes || '-'}
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Reject reason input */}
        {showRejectForm && (
          <div className="trade-detail-reject-form">
            <textarea
              className="trade-detail-reject-textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t('modifications.detail.rejectReasonPlaceholder', 'Enter reason for rejection...')}
              rows={2}
              autoFocus
            />
          </div>
        )}

        {/* Action buttons */}
        <div className="trade-detail-footer">
          {!showRejectForm ? (
            <>
              <button
                className="modifications-btn-reject"
                onClick={() => setShowRejectForm(true)}
                disabled={submitting}
              >
                {t('modifications.detail.rejectBtn', 'Reject')}
              </button>
              <button
                className="modifications-btn-confirm"
                onClick={handleApprove}
                disabled={submitting}
              >
                {submitting ? t('common.loading', 'Loading...') : t('modifications.quoteApproval.approveBtn', 'Approve')}
              </button>
            </>
          ) : (
            <>
              <button
                className="mod-panel-btn-cancel"
                onClick={() => setShowRejectForm(false)}
                disabled={submitting}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="modifications-btn-reject"
                onClick={handleReject}
                disabled={submitting || !rejectReason.trim()}
              >
                {submitting ? t('common.loading', 'Loading...') : t('modifications.detail.confirmReject', 'Confirm Rejection')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradeDetailViewModal;
